import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { AboutModal } from './components/AboutModal';
import { Banner } from './components/Banner';
import { CanonicalTab } from './components/CanonicalTab';
import { DiagnosticsTab } from './components/DiagnosticsTab';
import { DocumentHeader } from './components/DocumentHeader';
import { DropOverlay, IntakeBlock, IntakeHint, type IntakeActions, type IntakeHintKind } from './components/Intake';
import { EnginePill } from './components/EnginePill';
import type { CompileUi, EngineUi } from './components/engineUi';
import type { AppliedFix, FindingActions } from './components/Findings';
import { changeText } from './components/FixPreview';
import { Menu } from './components/Menu';
import { Notebook } from './components/Notebook';
import { ReaderView } from './components/ReaderView';
import { RowsTab } from './components/RowsTab';
import { StatusBar } from './components/StatusBar';
import { LazyShapeSection } from './components/shape/LazyShapeSection';
import { StitchMapTab } from './components/StitchMapTab';
import { TabPanel, Tabs, type TabDef } from './components/Tabs';
import { Toast, type ToastMessage } from './components/Toast';
import { TopBar } from './components/TopBar';
import { WelcomePanel } from './components/WelcomePanel';
import { countBySeverity, groupDiagnostics, groupsByLine, type DiagnosticGroup } from './diagnostics/group';
import { diagnosticExplanation, diagnosticTitle } from './diagnostics/catalog';
import { PatternEditor, type LineDiagnosticsInput, type LineStatusInput, type PatternEditorHandle } from './editor/PatternEditor';
import {
  createEngine,
  engineKindFromUrl,
  isEngineUnavailableError,
  isStaleCompileError,
  type EngineClient,
  type EngineKind,
} from './engine';
import { diagnosticMessage } from './engine/localize';
import type { CompileResult, Fix } from './engine/types';
import { derivesFrom, expectedHint, loadExampleIndex, loadExampleText, sameText, type ExampleEntry } from './examples/loadExamples';
import { I18nContext, initialLang, translate, type I18n, type Lang } from './i18n';
import { analysisState } from './lib/analysis';
import { applyReplacement } from './lib/applyReplacement';
import { downloadJson, slugify } from './lib/download';
import { dragHasFiles, prefersTouch, readClipboard, readPatternFile, starterText } from './lib/intake';
import { buildReport, type ReportKind } from './lib/report';
import { readByIsland, rowTone, toneNoteKey } from './lib/rowStatus';
import { decodeShareHash, shareUrl } from './lib/share';
import { sizeName, sizesLabel } from './lib/sizes';
import {
  DRAFT_KEY,
  EXAMPLE_KEY,
  LANG_KEY,
  LINE_NUMBERS_KEY,
  ROWS_TABLE_KEY,
  TAB_KEY,
  VIEW_KEY,
  readStorage,
  removeStorage,
  writeStorage,
} from './lib/storage';
import { summarizeChanges } from './lib/tokenDiff';
import { looksHardWrapped, unwrapLines } from './lib/unwrap';
import { useTheme } from './theme/useTheme';
import { Icon } from './ui/Icon';

type TabId = 'rows' | 'diagnostics' | 'map' | 'shape' | 'canonical';
const TAB_IDS: readonly TabId[] = ['rows', 'diagnostics', 'map', 'shape', 'canonical'];
const COMPILE_DEBOUNCE_MS = 250;
const DRAFT_DEBOUNCE_MS = 400;
const COMPILE_OPTIONS = { withStitchMap: true, maxMapLayers: 40 } as const;

export function App() {
  const [lang, setLangState] = useState<Lang>(() => initialLang(readStorage(LANG_KEY), window.location.search));
  const i18n = useMemo<I18n>(() => ({ lang, t: (key, vars) => translate(lang, key, vars) }), [lang]);
  const { t } = i18n;
  const theme = useTheme();

  const initialKind = useMemo(() => engineKindFromUrl(window.location.search), []);
  const [engineKind, setEngineKind] = useState<EngineKind>(initialKind);
  const [mockReason, setMockReason] = useState<'explicit' | 'fallback' | null>(initialKind === 'mock' ? 'explicit' : null);
  const [mockBannerHidden, setMockBannerHidden] = useState(false);
  const [engineUi, setEngineUi] = useState<EngineUi>({ status: 'loading', progress: null });
  const [compileUi, setCompileUi] = useState<CompileUi>({ status: 'idle' });
  const [result, setResult] = useState<CompileResult | null>(null);
  const [resultSnapshot, setResultSnapshot] = useState<number | null>(null);
  const [selectedSize, setSelectedSize] = useState<number | null>(null);
  const [tab, setTab] = useState<TabId>(() => {
    const stored = readStorage(TAB_KEY);
    return TAB_IDS.find((id) => id === stored) ?? 'diagnostics';
  });
  const [compact, setCompactState] = useState(() => readStorage(VIEW_KEY) === 'compact');
  const [lineNumbers, setLineNumbersState] = useState(() => readStorage(LINE_NUMBERS_KEY) === '1');
  const [tableView, setTableViewState] = useState(() => readStorage(ROWS_TABLE_KEY) === '1');
  const [readerMode, setReaderMode] = useState(false);
  const [compiledText, setCompiledText] = useState('');
  const [applied, setApplied] = useState<AppliedFix[]>([]);
  const [pasteOffer, setPasteOffer] = useState<string | null>(null);
  const [examples, setExamples] = useState<ExampleEntry[] | null>(null);
  const [currentExample, setCurrentExample] = useState('');
  const [draftSaved, setDraftSaved] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const [editorEmpty, setEditorEmpty] = useState(true);
  /** Where to paste, or why a file was not loaded (shown above the editor until text arrives). */
  const [intakeHint, setIntakeHint] = useState<IntakeHintKind | null>(null);
  /** A file is being dragged over the manuscript. */
  const [dropActive, setDropActive] = useState(false);
  /** The first text (share link, draft, or nothing) is in place: her own can come in without being overwritten. */
  const [textSettled, setTextSettled] = useState(false);
  // Phones are read-oriented: the editor starts folded away (the results come first).
  const [editorCollapsed, setEditorCollapsed] = useState(() => {
    try {
      return window.matchMedia('(max-width: 639px)').matches;
    } catch {
      return false;
    }
  });

  const editorRef = useRef<PatternEditorHandle>(null);
  const editorSectionRef = useRef<HTMLElement>(null);
  const dragDepth = useRef(0);
  const engineRef = useRef<EngineClient | null>(null);
  const compileTimer = useRef<number | undefined>(undefined);
  const draftTimer = useRef<number | undefined>(undefined);
  const textReady = useRef(false);
  /** Text of the last example loaded into the editor (null: text came from a link, a draft or the user). */
  const lastLoadedText = useRef<string | null>(null);
  const toastSeq = useRef(0);
  const appliedSeq = useRef(0);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = translate(lang, 'meta.title');
    document.querySelector('meta[name="description"]')?.setAttribute('content', translate(lang, 'meta.description'));
  }, [lang]);

  // `?lang=en` in the address is remembered like the toggle.
  useEffect(() => {
    const fromUrl = new URLSearchParams(window.location.search).get('lang');
    if (fromUrl === 'en' || fromUrl === 'fr') writeStorage(LANG_KEY, fromUrl);
  }, []);

  const setCompact = useCallback((v: boolean) => {
    setCompactState(v);
    writeStorage(VIEW_KEY, v ? 'compact' : 'notebook');
  }, []);
  const setLineNumbers = useCallback((v: boolean) => {
    setLineNumbersState(v);
    writeStorage(LINE_NUMBERS_KEY, v ? '1' : '0');
  }, []);
  const setTableView = useCallback((v: boolean) => {
    setTableViewState(v);
    writeStorage(ROWS_TABLE_KEY, v ? '1' : '0');
  }, []);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    writeStorage(LANG_KEY, l);
  }, []);

  const showToast = useCallback((text: string, tone: ToastMessage['tone'] = 'info') => {
    setToast({ id: ++toastSeq.current, text, tone });
  }, []);
  const clearToast = useCallback(() => setToast(null), []);

  // ------------------------------------------------------------------ compile
  const compileNow = useCallback(async () => {
    window.clearTimeout(compileTimer.current);
    const engine = engineRef.current;
    const editor = editorRef.current;
    if (!engine || !editor || !textReady.current) return;
    const snap = editor.beginSnapshot();
    setCompileUi((prev) => ({ status: 'compiling', lastMs: prev.status === 'done' ? prev.ms : undefined }));
    const t0 = performance.now();
    try {
      const res = await engine.compile(snap.text, COMPILE_OPTIONS);
      if (engineRef.current !== engine) return;
      setResult(res);
      setResultSnapshot(snap.id);
      setCompiledText(snap.text);
      // An applied fix can be undone only while its corrected line is still there.
      const lines = new Set(snap.text.split('\n').map((l) => l.trimEnd()));
      setApplied((prev) => (prev.every((a) => lines.has(a.newText.trimEnd())) ? prev : prev.filter((a) => lines.has(a.newText.trimEnd()))));
      setCompileUi({ status: 'done', ms: res.timing_ms ?? performance.now() - t0 });
    } catch (err) {
      if (isStaleCompileError(err) || engineRef.current !== engine) return;
      setCompileUi({ status: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  const dropExampleAssociation = useCallback(() => {
    lastLoadedText.current = null;
    setCurrentExample('');
    removeStorage(EXAMPLE_KEY);
  }, []);

  const saveDraftNow = useCallback(() => {
    window.clearTimeout(draftTimer.current);
    draftTimer.current = undefined;
    const editor = editorRef.current;
    if (!editor) return;
    const text = editor.getText();
    setDraftSaved(writeStorage(DRAFT_KEY, text));
    // The user replaced the example with their own text: stop presenting it as that example.
    if (lastLoadedText.current !== null && !derivesFrom(text, lastLoadedText.current)) dropExampleAssociation();
  }, [dropExampleAssociation]);

  const onEditorChange = useCallback(
    ({ empty }: { empty: boolean }) => {
      setEditorEmpty(empty);
      if (!empty) setIntakeHint(null);
      // Emptying the editor (select all + delete/type) ends the link with the loaded example at once.
      if (empty && lastLoadedText.current !== null && textReady.current) dropExampleAssociation();
      window.clearTimeout(compileTimer.current);
      compileTimer.current = window.setTimeout(() => void compileNow(), COMPILE_DEBOUNCE_MS);
      if (!textReady.current) return;
      window.clearTimeout(draftTimer.current);
      draftTimer.current = window.setTimeout(saveDraftNow, DRAFT_DEBOUNCE_MS);
    },
    [compileNow, saveDraftNow, dropExampleAssociation],
  );

  // Never lose the last keystrokes: write a pending draft when the page is hidden or unloaded.
  useEffect(() => {
    const flush = () => {
      if (draftTimer.current !== undefined) saveDraftNow();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [saveDraftNow]);

  useEffect(
    () => () => {
      window.clearTimeout(compileTimer.current);
      window.clearTimeout(draftTimer.current);
    },
    [],
  );

  // ------------------------------------------------------------------ engine lifecycle
  useEffect(() => {
    let cancelled = false;
    let created: EngineClient | null = null;
    setEngineUi({ status: 'loading', progress: null });
    void createEngine(engineKind).then(async (engine) => {
      if (cancelled) {
        engine.dispose?.();
        return;
      }
      created = engine;
      engineRef.current = engine;
      try {
        const info = await engine.ready((progress) => {
          if (!cancelled && progress.stage !== 'ready') setEngineUi({ status: 'loading', progress });
        });
        if (cancelled) return;
        setEngineUi({ status: 'ready', info });
        void compileNow();
      } catch (err) {
        if (cancelled) return;
        if (isEngineUnavailableError(err) && err.reason === 'wheel-missing' && import.meta.env.DEV) {
          setMockReason('fallback');
          setEngineKind('mock');
          return;
        }
        setEngineUi({ status: 'unavailable', message: err instanceof Error ? err.message : String(err) });
      }
    });
    return () => {
      cancelled = true;
      if (created) {
        created.dispose?.();
        if (engineRef.current === created) engineRef.current = null;
      }
    };
  }, [engineKind, compileNow]);

  // ------------------------------------------------------------------ initial text: link > draft > first example
  useEffect(() => {
    let cancelled = false;
    const fromHash = decodeShareHash(window.location.hash);
    const draft = fromHash === null ? readStorage(DRAFT_KEY) : null;
    void (async () => {
      let list: ExampleEntry[] = [];
      try {
        list = await loadExampleIndex();
      } catch {
        list = [];
      }
      if (cancelled) return;
      setExamples(list);
      const text = fromHash ?? (draft && draft.trim() !== '' ? draft : null);
      let exampleId = '';
      let exampleText: string | null = null;
      // First visit (no link, no draft): the editor stays empty and the welcome panel offers the examples.
      if (fromHash === null && text !== null) {
        // The draft remembers which example it started from (scenario strip, replace confirmation).
        const source = list.find((e) => e.id === readStorage(EXAMPLE_KEY));
        if (source) {
          exampleId = source.id;
          exampleText = await loadExampleText(source).catch(() => null);
        }
      }
      if (cancelled) return;
      setCurrentExample(exampleId);
      lastLoadedText.current = exampleText;
      if (exampleId) writeStorage(EXAMPLE_KEY, exampleId);
      else removeStorage(EXAMPLE_KEY);
      if (fromHash !== null) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        showToast(translate(initialLang(readStorage(LANG_KEY)), 'toast.restoredFromLink'), 'success');
      } else if (text !== null && text === draft) {
        setDraftSaved(true);
      }
      textReady.current = true;
      editorRef.current?.replaceAll(text ?? '', { history: false });
      setTextSettled(true);
      void compileNow();
    })();
    return () => {
      cancelled = true;
    };
  }, [compileNow, showToast]);

  // A share link pasted into an already open tab only changes the hash: load it too.
  useEffect(() => {
    const onHash = () => {
      const text = decodeShareHash(window.location.hash);
      if (text === null || !textReady.current) return;
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      editorRef.current?.replaceAll(text);
      dropExampleAssociation();
      showToast(t('toast.restoredFromLink'), 'success');
      void compileNow();
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [compileNow, dropExampleAssociation, showToast, t]);

  // ------------------------------------------------------------------ derived data
  const sizes = useMemo(() => result?.document.sizes ?? [], [result]);
  const effectiveSize = selectedSize !== null && sizes.some((s) => s.index === selectedSize) ? selectedSize : null;
  const groups = useMemo(() => (result ? groupDiagnostics(result.diagnostics, result.document.sizes.length) : []), [result]);
  const groupCounts = useMemo(() => countBySeverity(groups), [groups]);
  const analysis = result ? analysisState(result) : null;

  // Push diagnostics into the editor (gutter, line tint, hover), localized.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || resultSnapshot === null || !result) return;
    const lines: LineDiagnosticsInput[] = [...groupsByLine(groups)].map(([line_no, entry]) => ({
      line_no,
      severity: entry.severity,
      items: entry.groups.map((g) => ({
        severity: g.severity,
        code: g.code,
        title: diagnosticTitle(g.code, lang),
        explanation: diagnosticExplanation(g.code, lang),
        sizesLabel: sizesLabel(g.sizes, result, t, lang),
        messages: g.messages.map((m) => {
          const d = g.diagnostics.find((x) => x.message === m.text);
          const text = d ? diagnosticMessage(d, lang).text : m.text;
          return g.messages.length > 1 ? t('diag.sizedMessage', { sizes: sizesLabel(m.sizes, result, t, lang), text }) : text;
        }),
      })),
    }));
    editor.commitDiagnostics(resultSnapshot, lines);
  }, [groups, result, resultSnapshot, lang, t]);

  // Mark every row with its reading tone for the selected size (a thin rule in the paper margin, shaped and
  // coloured by the tone, the reason on hover; a row verified by a rule has nothing to say).
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || resultSnapshot === null || !result) return;
    const lines: LineStatusInput[] = [];
    for (const row of result.rows) {
      const tone = rowTone(row, effectiveSize);
      if (!tone) continue;
      const key = toneNoteKey(tone, readByIsland(row));
      lines.push({ line_no: row.line_no, tone, label: key ? t(key) : '' });
    }
    editor.commitRowStatuses(resultSnapshot, lines);
  }, [result, resultSnapshot, effectiveSize, t]);

  // ------------------------------------------------------------------ actions
  const jumpToLine = useCallback((line: number) => {
    setEditorCollapsed(false);
    setReaderMode(false);
    // The editor may have just been unhidden (reader view, folded on a phone): reveal after layout.
    requestAnimationFrame(() => editorRef.current?.revealLine(line));
  }, []);

  const applyFix = useCallback(
    (fix: Fix) => {
      const editor = editorRef.current;
      const r = fix.replacement;
      if (!editor || !r) return;
      const current = editor.currentLineFor(r.line_no);
      const outcome = current === null ? null : applyReplacement(editor.getText(), { ...r, line_no: current });
      if (!outcome || !outcome.ok) {
        showToast(t('diag.lineChanged'), 'warning');
        return;
      }
      editor.replaceRange(outcome.from, outcome.to, outcome.insert);
      const names = result?.document.sizes.map((s) => sizeName(result, s.index, lang)) ?? [];
      const summary = summarizeChanges(r.old_text, r.new_text, names)
        .map((c) => changeText(c, t))
        .join(' ; ');
      setApplied((prev) => [
        ...prev.slice(-4),
        { id: `${fix.id}#${++appliedSeq.current}`, fix, line: current ?? r.line_no, oldText: r.old_text, newText: r.new_text, summary },
      ]);
      showToast(t('diag.applied', { n: current ?? r.line_no }), 'success');
      void compileNow();
    },
    [compileNow, showToast, t, result, lang],
  );

  const undoApplied = useCallback(
    (a: AppliedFix) => {
      const editor = editorRef.current;
      if (!editor) return;
      const text = editor.getText();
      const lines = text.split('\n');
      let best = -1;
      lines.forEach((l, i) => {
        if (l.trimEnd() !== a.newText.trimEnd()) return;
        if (best < 0 || Math.abs(i + 1 - a.line) < Math.abs(best + 1 - a.line)) best = i;
      });
      setApplied((prev) => prev.filter((x) => x.id !== a.id));
      const outcome = best < 0 ? null : applyReplacement(text, { line_no: best + 1, old_text: a.newText, new_text: a.oldText });
      if (!outcome || !outcome.ok) {
        showToast(t('find.undoFailed'), 'warning');
        return;
      }
      editor.replaceRange(outcome.from, outcome.to, outcome.insert);
      showToast(t('find.undone', { n: best + 1 }), 'success');
      void compileNow();
    },
    [compileNow, showToast, t],
  );

  const dismissApplied = useCallback((a: AppliedFix) => setApplied((prev) => prev.filter((x) => x.id !== a.id)), []);

  const copyReport = useCallback(
    async (kind: ReportKind, group?: DiagnosticGroup) => {
      const text = buildReport({
        kind,
        lang,
        group,
        result,
        engineVersion: result?.engine_version ?? (engineUi.status === 'ready' ? engineUi.info.engineVersion : null),
        schemaVersion: result?.schema_version ?? (engineUi.status === 'ready' ? engineUi.info.schemaVersion : null),
      });
      try {
        await navigator.clipboard.writeText(text);
        showToast(t('toast.reportCopied'), 'success');
      } catch {
        showToast(t('toast.reportFailed'), 'warning');
      }
    },
    [lang, result, engineUi, showToast, t],
  );

  const findingActions = useMemo<FindingActions>(
    () => ({ onJump: jumpToLine, onApply: applyFix, onReport: (g, kind) => void copyReport(kind, g) }),
    [jumpToLine, applyFix, copyReport],
  );

  const onPaste = useCallback((text: string) => {
    setPasteOffer(looksHardWrapped(text) ? text : null);
  }, []);

  const rejoinPaste = useCallback(() => {
    const editor = editorRef.current;
    const offer = pasteOffer;
    setPasteOffer(null);
    if (!editor || !offer) return;
    const at = editor.getText().indexOf(offer);
    if (at < 0) {
      showToast(t('toast.pdfChanged'), 'warning');
      return;
    }
    const { text, joined } = unwrapLines(offer);
    editor.replaceRange(at, at + offer.length, text);
    showToast(t('toast.pdfJoined', { n: joined }), 'success');
    void compileNow();
  }, [pasteOffer, compileNow, showToast, t]);

  const printNow = useCallback(() => {
    document.body.dataset.print = readerMode ? 'reader' : 'notebook';
    window.print();
  }, [readerMode]);

  const loadExample = useCallback(
    async (id: string) => {
      const entry = examples?.find((e) => e.id === id);
      const editor = editorRef.current;
      if (!entry || !editor) return;
      // Synchronous, before any await: a refusal lets the controlled <select> snap back.
      const current = editor.getText();
      const edited = current.trim() !== '' && (lastLoadedText.current === null || !sameText(current, lastLoadedText.current));
      if (edited && !window.confirm(t('examples.confirmReplace', { title: entry.title }))) return;
      try {
        const text = await loadExampleText(entry);
        textReady.current = true;
        editorRef.current?.replaceAll(text);
        lastLoadedText.current = text;
        setCurrentExample(id);
        writeStorage(EXAMPLE_KEY, id);
        showToast(t('toast.exampleLoaded', { title: entry.title }), 'success');
        void compileNow();
      } catch {
        showToast(t('toast.exampleFailed'), 'error');
      }
    },
    [examples, compileNow, showToast, t],
  );

  const copyLink = useCallback(async () => {
    const text = editorRef.current?.getText() ?? '';
    const url = shareUrl(text, { origin: window.location.origin, pathname: window.location.pathname, search: '' });
    try {
      await navigator.clipboard.writeText(url);
      showToast(t('toast.linkCopied'), 'success');
    } catch {
      window.history.replaceState(null, '', url);
      showToast(t('toast.linkFailed'), 'warning');
    }
  }, [showToast, t]);

  const downloadReport = useCallback(() => {
    if (!result) {
      showToast(t('toast.noResult'), 'warning');
      return;
    }
    downloadJson(`frogless-${slugify(result.document.title ?? 'patron')}.json`, result);
  }, [result, showToast, t]);

  const clearText = useCallback(() => {
    textReady.current = true;
    editorRef.current?.replaceAll('');
    editorRef.current?.focus();
    dropExampleAssociation();
    showToast(t('toast.cleared'));
  }, [dropExampleAssociation, showToast, t]);

  const forgetDraft = useCallback(() => {
    window.clearTimeout(draftTimer.current);
    draftTimer.current = undefined;
    removeStorage(DRAFT_KEY);
    removeStorage(EXAMPLE_KEY);
    setDraftSaved(false);
    showToast(t('toast.draftForgotten'));
  }, [showToast, t]);

  // ------------------------------------------------------------------ her own pattern: paste, open a file, a starter
  /** Unfolds the manuscript (phone), leaves « Ma taille », focuses the editor and brings it into view. */
  const revealEditor = useCallback(() => {
    // Synchronous, so that focus() still happens inside the tap: mobile keyboards only open then.
    flushSync(() => {
      setEditorCollapsed(false);
      setReaderMode(false);
    });
    editorRef.current?.focus();
    const section = editorSectionRef.current;
    const top = section?.getBoundingClientRect().top ?? 0;
    if (section && (top < 0 || top > window.innerHeight / 2)) section.scrollIntoView({ block: 'start' });
  }, []);

  /** Asks before `name` replaces text she wrote (an untouched example is replaced without asking). */
  const refusesReplace = useCallback(
    (name: string) => {
      const current = editorRef.current?.getText() ?? '';
      const edited = current.trim() !== '' && (lastLoadedText.current === null || !sameText(current, lastLoadedText.current));
      return edited && !window.confirm(t('intake.confirmReplace', { name }));
    },
    [t],
  );

  /** Her own text in the editor: no longer presented as an example, checked at once. */
  const loadOwnText = useCallback(
    (text: string, opts: { cursor: 'start' | 'end'; pasted?: boolean }) => {
      const editor = editorRef.current;
      if (!editor) return;
      textReady.current = true;
      editor.replaceAll(text, { cursor: opts.cursor });
      dropExampleAssociation();
      setIntakeHint(null);
      // Pasted from a PDF: the same « Recoller les lignes » offer as after a Ctrl+V.
      setPasteOffer(opts.pasted && looksHardWrapped(text) ? text : null);
      void compileNow();
    },
    [compileNow, dropExampleAssociation],
  );

  const pasteFromClipboard = useCallback(() => {
    // First, while the click still counts: browsers only give the clipboard during a user gesture.
    const pending = readClipboard();
    setIntakeHint(null);
    revealEditor();
    void pending.then((outcome) => {
      const editor = editorRef.current;
      // She pasted or typed meanwhile: her text wins.
      if (!editor || editor.getText().trim() !== '') return;
      if (outcome.kind === 'text') {
        loadOwnText(outcome.text, { cursor: 'end', pasted: true });
        editor.focus();
      } else {
        setIntakeHint(outcome.kind === 'empty' ? 'clipboardEmpty' : prefersTouch() ? 'pasteTouch' : 'paste');
      }
    });
  }, [revealEditor, loadOwnText]);

  const openFile = useCallback(
    async (file: File) => {
      setIntakeHint(null);
      const outcome = await readPatternFile(file);
      if (outcome.kind !== 'text') {
        // A PDF, a .docx, an image…: explained above the editor, where the text should be pasted.
        revealEditor();
        setIntakeHint(outcome.kind);
        return;
      }
      if (refusesReplace(outcome.name)) return;
      revealEditor();
      loadOwnText(outcome.text, { cursor: 'start' });
      showToast(t('intake.fileLoaded', { name: outcome.name }), 'success');
    },
    [revealEditor, refusesReplace, loadOwnText, showToast, t],
  );

  // Offered by the welcome only, so over an empty editor: nothing to confirm.
  const startFromBlank = useCallback(() => {
    revealEditor();
    loadOwnText(starterText(lang), { cursor: 'end' });
    editorRef.current?.focus();
  }, [revealEditor, loadOwnText, lang]);

  const intakeActions = useMemo<IntakeActions>(
    () => ({ onPaste: pasteFromClipboard, onFile: (file) => void openFile(file), onStarter: startFromBlank }),
    [pasteFromClipboard, openFile, startFromBlank],
  );

  // « Nouveau patron » in the editor's header: the same three entries, reachable at any time (the welcome panel is gone
  // once a text is loaded). Over an edited text, the starter asks before replacing it, like a file does.
  const newMenu = useRef<HTMLDetailsElement>(null);
  const closeNewMenu = useCallback(() => {
    if (newMenu.current) newMenu.current.open = false;
  }, []);
  const headerIntakeActions = useMemo<IntakeActions>(
    () => ({
      onPaste: () => {
        closeNewMenu();
        pasteFromClipboard();
      },
      onFile: (file) => {
        closeNewMenu();
        void openFile(file);
      },
      onStarter: () => {
        closeNewMenu();
        if (refusesReplace(t('intake.starter'))) return;
        startFromBlank();
      },
    }),
    [closeNewMenu, pasteFromClipboard, openFile, refusesReplace, startFromBlank, t],
  );

  // A text file dropped on the manuscript replaces the text (read here, never sent).
  const onDragEnter = useCallback((e: ReactDragEvent) => {
    if (!dragHasFiles(e.dataTransfer)) return;
    dragDepth.current += 1;
    setDropActive(true);
  }, []);
  const onDragLeave = useCallback((e: ReactDragEvent) => {
    if (!dragHasFiles(e.dataTransfer)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDropActive(false);
  }, []);
  const onDragOver = useCallback((e: ReactDragEvent) => {
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);
  const onDrop = useCallback(
    (e: ReactDragEvent) => {
      if (!dragHasFiles(e.dataTransfer)) return;
      // Captured before CodeMirror, which would insert the file where it lands: a file replaces the text.
      e.preventDefault();
      dragDepth.current = 0;
      setDropActive(false);
      const file = e.dataTransfer.files[0];
      // Not before the draft (or shared link) is back in the editor: it would overwrite the file.
      if (file && textSettled) void openFile(file);
    },
    [openFile, textSettled],
  );

  // A file dropped beside the manuscript must not replace the page (the browser would open it).
  useEffect(() => {
    const onOver = (e: DragEvent) => {
      if (!dragHasFiles(e.dataTransfer) || editorSectionRef.current?.contains(e.target as Node)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    const onDropOutside = (e: DragEvent) => {
      if (dragHasFiles(e.dataTransfer)) e.preventDefault();
    };
    window.addEventListener('dragover', onOver);
    window.addEventListener('drop', onDropOutside);
    return () => {
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('drop', onDropOutside);
    };
  }, []);

  const changeTab = useCallback((id: TabId) => {
    setTab(id);
    writeStorage(TAB_KEY, id);
  }, []);

  // ------------------------------------------------------------------ view
  const isMock = engineKind === 'mock';
  const currentEntry = examples?.find((e) => e.id === currentExample) ?? null;
  const scenarioHint = currentEntry ? expectedHint(currentEntry, t) : null;
  const scenarioText = currentEntry ? (lang === 'fr' ? currentEntry.description_fr : currentEntry.description_en) : '';
  const errorCount = groupCounts.error;
  const readerSize = effectiveSize ?? 0;
  const showReader = readerMode && !editorEmpty;

  const toggleReader = useCallback(
    (on: boolean) => {
      setReaderMode(on);
      if (on) {
        setEditorCollapsed(false);
        if (selectedSize === null && sizes.length > 1) setSelectedSize(0);
      }
    },
    [selectedSize, sizes.length],
  );

  // Print only what is on screen: the notebook (report) or "Ma taille".
  useEffect(() => {
    document.body.dataset.print = showReader ? 'reader' : 'notebook';
  }, [showReader]);

  // A printed report shows every finding: folded sections open for the print, then fold back.
  useEffect(() => {
    const opened: HTMLDetailsElement[] = [];
    const before = () => {
      document.querySelectorAll<HTMLDetailsElement>('[data-print-area] details:not([open])').forEach((d) => {
        if (d.closest('[data-print-hide]')) return;
        d.open = true;
        opened.push(d);
      });
    };
    const after = () => {
      for (const d of opened.splice(0)) d.open = false;
    };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);

  const tabs: TabDef<TabId>[] = [
    { id: 'rows', label: t('tabs.rows'), badge: result ? <Count n={result.rows.length || (result.module_trace?.length ?? 0)} /> : null },
    {
      id: 'diagnostics',
      label: t('tabs.diagnostics'),
      badge: result ? (
        <Count
          n={groups.length === 0 && analysis === 'none' ? '?' : groups.length}
          tone={errorCount > 0 ? 'error' : groups.length > 0 || analysis === 'none' ? 'warning' : 'ok'}
        />
      ) : null,
    },
    { id: 'map', label: t('tabs.map') },
    { id: 'shape', label: t('tabs.shape') },
    { id: 'canonical', label: t('tabs.canonical') },
  ];

  let panel: ReactNode = <p className="p-6 text-center text-sm text-muted">{t('doc.waiting')}</p>;
  if (result) {
    if (tab === 'rows') panel = <RowsTab result={result} selectedSize={effectiveSize} activeLine={activeLine} onJump={jumpToLine} />;
    else if (tab === 'diagnostics')
      panel = (
        <DiagnosticsTab
          result={result}
          analysis={analysis ?? 'none'}
          groups={groups}
          selectedSize={effectiveSize}
          actions={findingActions}
          applied={applied}
          onUndo={undoApplied}
          onDismissApplied={dismissApplied}
        />
      );
    else if (tab === 'map')
      panel = (
        <div className="p-4">
          <StitchMapTab result={result} selectedSize={effectiveSize} onSize={setSelectedSize} />
        </div>
      );
    else if (tab === 'shape')
      panel = (
        <div className="p-4">
          <LazyShapeSection result={result} selectedSize={effectiveSize} onSize={setSelectedSize} />
        </div>
      );
    else panel = <CanonicalTab result={result} activeLine={activeLine} onJump={jumpToLine} />;
  }

  const schemaVersion = result?.schema_version ?? (engineUi.status === 'ready' ? engineUi.info.schemaVersion : null);
  const engineVersion = result?.engine_version ?? (engineUi.status === 'ready' ? engineUi.info.engineVersion : null);

  let right: ReactNode;
  if (editorEmpty) right = <WelcomePanel examples={examples} onOpen={(id) => void loadExample(id)} intake={intakeActions} intakeReady={textSettled} />;
  else if (compact)
    right = (
      <div className="flex min-h-0 flex-1 flex-col" data-print-area="notebook" data-testid="compact-view">
        <DocumentHeader result={result} counts={groupCounts} groups={groups} fallbackTitle={currentEntry?.title} analysis={analysis} variant="compact" />
        <Tabs tabs={tabs} active={tab} onChange={changeTab} ariaLabel={t('tabs.aria')} idPrefix="results" />
        {TAB_IDS.map((id) => (
          <TabPanel key={id} idPrefix="results" id={id} active={tab === id}>
            {tab === id ? panel : null}
          </TabPanel>
        ))}
      </div>
    );
  else if (result && analysis)
    right = (
      <Notebook
        result={result}
        analysis={analysis}
        groups={groups}
        counts={groupCounts}
        fallbackTitle={currentEntry?.title}
        selectedSize={effectiveSize}
        onSize={setSelectedSize}
        activeLine={activeLine}
        actions={findingActions}
        applied={applied}
        onUndo={undoApplied}
        onDismissApplied={dismissApplied}
        tableView={tableView}
        onTableView={setTableView}
      />
    );
  else right = <p className="p-8 text-sm text-muted">{t('doc.waiting')}</p>;

  return (
    <I18nContext.Provider value={i18n}>
      <div className="flex min-h-full flex-col bg-bg lg:h-full lg:overflow-hidden">
        <TopBar
          onLang={setLang}
          theme={theme.pref}
          themeScheme={theme.scheme}
          onTheme={theme.setPref}
          sizes={editorEmpty ? [] : sizes}
          selectedSize={effectiveSize}
          onSize={setSelectedSize}
          enginePill={<EnginePill engine={engineUi} compile={compileUi} isMock={isMock} />}
          onCopyLink={() => void copyLink()}
          onDownload={downloadReport}
          canDownload={result !== null}
          onClear={clearText}
          onPrint={printNow}
          readerMode={showReader}
          onReaderMode={toggleReader}
          canRead={!editorEmpty}
          compact={compact}
          onCompact={setCompact}
        />
        {mockReason && !mockBannerHidden && (
          <Banner tone={mockReason === 'fallback' ? 'warning' : 'info'} onDismiss={() => setMockBannerHidden(true)} testId="mock-banner">
            {mockReason === 'fallback' ? t('banner.mockFallback') : t('banner.mock')}
          </Banner>
        )}
        {engineUi.status === 'unavailable' && !isMock && (
          <Banner
            tone="error"
            testId="engine-unavailable"
            action={
              <button
                type="button"
                className="btn-secondary h-7"
                onClick={() => {
                  setMockReason('explicit');
                  setEngineKind('mock');
                }}
              >
                {t('banner.useMock')}
              </button>
            }
          >
            {t('banner.unavailable', { message: engineUi.message })}
          </Banner>
        )}
        {compileUi.status === 'error' && (
          <Banner tone="error" testId="compile-error">
            {t('banner.compileError', { message: compileUi.message })}
          </Banner>
        )}

        <main className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <section
            ref={editorSectionRef}
            aria-label={t('editor.title')}
            className={`relative flex min-h-0 flex-col border-b border-line bg-editor lg:h-auto lg:w-[52%] lg:border-b-0 lg:border-r ${
              editorCollapsed ? 'h-auto sm:h-[60vh] sm:min-h-[18rem]' : 'h-[60vh] min-h-[18rem] sm:h-[62vh]'
            } ${showReader ? '' : 'print:hidden'}`}
            data-print-area="reader"
            data-toast-anchor
            data-drop-active={dropActive || undefined}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOverCapture={onDragOver}
            onDropCapture={onDrop}
          >
            {dropActive && <DropOverlay />}
            <div className="flex min-h-12 shrink-0 flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 sm:px-6" data-print-hide>
              <h2 className="font-display text-lg font-[460] text-fg">{t('editor.title')}</h2>
              <details ref={newMenu} className="relative" data-testid="new-pattern">
                <summary
                  className="flex h-8 cursor-pointer list-none items-center gap-1.5 rounded-control border border-line px-2.5 text-xs text-fg hover:border-line-strong [&::-webkit-details-marker]:hidden"
                  title={t('top.newHint')}
                >
                  <Icon name="plus" size={12} className="text-accent-strong" />
                  {t('top.new')}
                </summary>
                <div className="absolute left-0 z-20 mt-1 w-[min(22rem,calc(100vw-2rem))] rounded-card border border-line bg-surface p-3 shadow-soft-lg">
                  <IntakeBlock actions={headerIntakeActions} ready={textSettled} variant="menu" />
                </div>
              </details>
              <label className="order-last flex w-full min-w-0 items-center text-xs text-muted sm:order-none sm:w-auto">
                <span className="sr-only">{t('top.examples')}</span>
                <select
                  className="h-8 w-full min-w-0 rounded-control border border-line bg-transparent px-2 text-xs text-fg hover:border-line-strong sm:w-[15rem]"
                  value={currentExample}
                  onChange={(e) => void loadExample(e.target.value)}
                  disabled={!examples || examples.length === 0}
                  data-testid="example-picker"
                  aria-label={t('top.examples')}
                >
                  <option value="">{examples ? t('top.examplesPlaceholder') : t('top.examplesUnavailable')}</option>
                  {examples?.map((ex) => {
                    const hint = expectedHint(ex, t);
                    return (
                      <option key={ex.id} value={ex.id} title={lang === 'fr' ? ex.description_fr : ex.description_en}>
                        {ex.title} ({ex.lang.toUpperCase()}
                        {ex.sizes.length ? ` · ${ex.sizes.join(', ')}` : ''}){hint ? ` — ${hint}` : ''}
                      </option>
                    );
                  })}
                </select>
              </label>
              {draftSaved ? (
                <span
                  className="inline-flex items-center gap-1 rounded-pill py-0.5 pl-2 pr-1 text-2xs font-semibold text-accent-strong"
                  title={t('editor.draftTitle')}
                  data-testid="draft-indicator"
                >
                  <Icon name="lock" size={10} />
                  <span className="hidden sm:inline">{t('editor.draft')}</span>
                  <span className="sm:hidden">{t('editor.draftShort')}</span>
                  <button
                    type="button"
                    onClick={forgetDraft}
                    className="rounded-pill p-0.5 hover:bg-accent/15"
                    aria-label={t('editor.draftForget')}
                    title={t('editor.draftForget')}
                  >
                    <Icon name="x" size={9} />
                  </button>
                </span>
              ) : (
                <span className="text-2xs text-subtle">{t('editor.draftOff')}</span>
              )}
              <span className="ml-auto hidden text-2xs text-subtle 2xl:inline">{t('editor.hint')}</span>
              <span className="ml-auto 2xl:ml-0">
                <Menu
                  label={t('editor.display')}
                  icon="sliders"
                  showLabel
                  className="btn-quiet h-7 text-2xs"
                  testId="display-menu"
                  items={[
                    {
                      id: 'lines',
                      label: t('editor.lineNumbers'),
                      checked: lineNumbers,
                      onSelect: () => setLineNumbers(!lineNumbers),
                      testId: 'toggle-line-numbers',
                    },
                  ]}
                />
              </span>
              <button
                type="button"
                className="btn-secondary h-7 px-2.5 sm:hidden"
                aria-expanded={!editorCollapsed}
                aria-controls="pattern-editor"
                onClick={() => setEditorCollapsed((c) => !c)}
                data-testid="editor-toggle"
              >
                <Icon name={editorCollapsed ? 'chevronDown' : 'chevronUp'} size={12} />
                {editorCollapsed ? t('editor.show') : t('editor.hide')}
              </button>
            </div>
            {currentEntry && (scenarioText || scenarioHint) && !showReader && (
              <div
                className="flex shrink-0 items-start gap-3 bg-heather/[0.07] px-4 py-2 text-2xs leading-relaxed sm:px-6"
                data-testid="example-scenario"
                title={scenarioText}
                data-print-hide
              >
                <span className="font-display mt-px shrink-0 text-xs italic text-fg">{t('examples.scenario')}</span>
                <span className="line-clamp-2 min-w-0 flex-1 text-muted sm:line-clamp-none">{scenarioText}</span>
                {scenarioHint && <span className="shrink-0 rounded-pill bg-surface px-2 py-px font-bold text-fg">{scenarioHint}</span>}
              </div>
            )}
            <div role="status" aria-live="polite" className="shrink-0" data-print-hide>
              {intakeHint && !showReader && <IntakeHint kind={intakeHint} onDismiss={() => setIntakeHint(null)} />}
            </div>
            {pasteOffer && !showReader && (
              <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 bg-info/[0.07] px-4 py-2 text-xs sm:px-6" role="status" data-testid="pdf-offer">
                <Icon name="paste" size={14} className="text-info" />
                <span className="min-w-0 flex-1">
                  <strong className="font-semibold text-fg">{t('editor.pdfTitle')}</strong> <span className="text-muted">{t('editor.pdfBody')}</span>
                </span>
                <button type="button" className="btn-primary h-7" onClick={rejoinPaste} data-testid="pdf-rejoin">
                  {t('editor.pdfJoin')}
                </button>
                <button type="button" className="btn-quiet h-7" onClick={() => setPasteOffer(null)}>
                  {t('editor.pdfDismiss')}
                </button>
              </div>
            )}
            {showReader && (
              <ReaderView
                text={compiledText}
                result={result}
                size={readerSize}
                onSize={setSelectedSize}
                onBack={() => setReaderMode(false)}
                onPrint={printNow}
              />
            )}
            <div id="pattern-editor" className={`min-h-0 flex-1 overflow-hidden ${editorCollapsed ? 'hidden sm:block' : ''} ${showReader ? '!hidden' : ''}`}>
              <PatternEditor
                ref={editorRef}
                className="h-full"
                onChange={onEditorChange}
                onCursorLine={setActiveLine}
                onCompileNow={() => void compileNow()}
                onPaste={onPaste}
                placeholder={t('editor.placeholder')}
                ariaLabel={t('editor.aria')}
                lineNumbers={lineNumbers}
              />
            </div>
          </section>

          <section
            aria-label={t('nb.title')}
            className={`flex min-h-[26rem] flex-col bg-bg lg:h-auto lg:min-h-0 lg:flex-1 ${compact ? 'h-[85vh]' : 'h-[88vh]'} ${showReader ? 'print:hidden' : ''}`}
          >
            {right}
          </section>
        </main>

        <StatusBar
          engine={engineUi}
          compile={compileUi}
          schemaVersion={schemaVersion}
          engineVersion={engineVersion}
          isMock={isMock}
          onAbout={() => setAboutOpen(true)}
        />
      </div>
      <Toast toast={toast} onDone={clearToast} />
      <AboutModal
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        onReport={() => void copyReport('general')}
        versions={[schemaVersion && `schema ${schemaVersion}`, engineVersion && `stitchcore ${engineVersion}`].filter(Boolean).join(' · ')}
      />
    </I18nContext.Provider>
  );
}

function Count({ n, tone = 'neutral' }: { n: number | string; tone?: 'neutral' | 'error' | 'warning' | 'ok' }) {
  const cls = {
    neutral: 'text-subtle',
    error: 'text-error',
    warning: 'text-warning',
    ok: 'text-accent-strong',
  }[tone];
  return <span className={`text-xs font-bold tabular ${cls}`}>{n}</span>;
}
